import React, { createContext, useContext, useMemo } from "react";

const passthrough = (value) => value;

const defaultValue = {
  enabled: false,
  amount: passthrough,
  client: passthrough,
  clientInitials: passthrough,
  contact: passthrough,
  date: passthrough,
  email: passthrough,
  invoice: (value) => value?.invoiceNumber || value?.id || value,
  number: passthrough,
  percentage: passthrough,
  phone: passthrough,
  text: passthrough,
  work: passthrough,
};

const PrivacyContext = createContext(defaultValue);

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const initialsFor = (name) => String(name || "Client")
  .split(/\s+/)
  .filter(Boolean)
  .map((part) => part[0])
  .join("")
  .slice(0, 2)
  .toUpperCase();

const currencyMask = (value) => {
  const text = String(value ?? "");
  const code = text.match(/\b(EUR|USD|GBP|CHF|CAD|AUD)\b/i)?.[1]?.toUpperCase();
  const symbol = text.match(/[€$£¥]/)?.[0];
  return `${symbol || code || "€"} ••••`;
};

export const PrivacyProvider = ({ enabled = false, clients = [], invoices = [], children }) => {
  const value = useMemo(() => {
    if (!enabled) return defaultValue;

    const client = (name) => name || "Client";
    const invoice = () => "Invoice ••••";
    const text = (source) => {
      let output = String(source ?? "");
      clients.forEach((record) => {
        const replacements = [
          [record.contact, "Private contact"],
          [record.email, "hidden@private"],
          [record.phone, "+•• •• ••• •••"],
          ...((record.contacts || []).flatMap((contact) => [[contact.name, "Private contact"], [contact.email, "hidden@private"], [contact.phone, "+•• •• ••• •••"]])),
        ];
        replacements.forEach(([actual, replacement]) => {
          if (actual) output = output.replace(new RegExp(escapeRegExp(actual), "gi"), replacement);
        });
      });
      invoices.forEach((record) => {
        if (record.invoiceNumber) output = output.replace(new RegExp(escapeRegExp(record.invoiceNumber), "g"), invoice(record));
      });
      return output
        .replace(/\b(EUR|USD|GBP|CHF|CAD|AUD)\s*-?[\d,.]+\b/gi, (_, code) => `${code.toUpperCase()} ••••`)
        .replace(/([€$£¥])\s*-?[\d,.]+/g, (_, symbol) => `${symbol} ••••`)
        .replace(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g, "hidden@private");
    };

    return {
      enabled: true,
      amount: currencyMask,
      client,
      clientInitials: initialsFor,
      contact: () => "Private contact",
      date: () => "Private date",
      email: () => "hidden@private",
      invoice,
      number: () => "••",
      percentage: () => "••%",
      phone: () => "+•• •• ••• •••",
      text,
      work: () => "Confidential services",
    };
  }, [clients, enabled, invoices]);

  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
};

export const usePrivacy = () => useContext(PrivacyContext);
